<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <component v-bind="componentAttrs"
             :is="appearance"
             :class="{ buttonDisabled: isBButtonDisabled }"
             v-on="$listeners"
  >
    <template v-if="isDownloadLink">
      <span class="align-items-center d-inline-flex"
            :class="{ 'w-100': isSduiListItem }"
            data-test="download-link-wrapper"
      >
        <span v-if="isExternal" class="sr-only">{{ $t('accessibility.external_url') }}</span>
        <template v-if="isSduiListItem">
          <slot />
          <sdui-portal-to>
            <sdui-icon data-test="download-icon-sdui-list-item" class="icon-download-o ml-1" />
          </sdui-portal-to>
        </template>
        <template v-else>
          <slot />
          <sdui-icon data-test="download-icon"
                     class="icon-download-o ml-1"
                     :class="{ 'icon-16': isLinkAppearance }"
          />
        </template>
      </span>
    </template>
    <template v-else>
      <template v-if="isExternal">
        <span class="sr-only">{{ $t('accessibility.external_url') }}</span>
        <template v-if="isSduiListItem">
          <slot />
          <sdui-portal-to>
            <sdui-icon data-test="externallink-icon-sdui-list-item" class="icon-externallink-o ml-1" />
          </sdui-portal-to>
        </template>
        <template v-else>
          <template v-if="forceHideExternalLinkIcon || hasDownloadAttribute">
            <slot />
          </template>
          <span v-else class="align-items-center d-inline-flex external-link-wrapper">
            <slot />
            <sdui-icon data-test="externallink-icon"
                       class="icon-externallink-o ml-1"
                       :class="{ 'icon-16': isLinkAppearance }"
            />
          </span>
        </template>
      </template>
      <slot v-else />
    </template>
  </component>
</template>

<script>
import { BLink, BButton, BDropdownItem } from 'bootstrap-vue'
import SduiPortalTo from 'src/components/miscellaneous/ui/common/SduiPortalTo.vue'

export default {
  name: 'SLink',

  components: {
    BLink,
    BButton,
    BDropdownItem,
    SduiPortalTo,
  },

  props: {
    href: {
      type: String,
      default: '',
    },

    appearance: {
      type: String,
      default: 'a',
      validator: (value) => ['b-link', 'b-button', 'b-dropdown-item', 'a'].includes(value),
    },

    forceHideDownloadIcon: {
      type: Boolean,
      default: false,
    },

    forceHideExternalLinkIcon: {
      type: Boolean,
      default: false,
    },

    isSduiListItem: {
      type: Boolean,
      default: false,
    },

    openExternally: {
      type: Boolean,
      default: null,
    },
  },

  computed: {
    componentAttrs() {
      return { href: this.href, target: this.target, rel: this.rel, ...this.$attrs }
    },

    isExternal() {
      if (this.openExternally !== null) {
        return this.openExternally
      }

      try {
        const link = new URL(this.href)
        const appUrl = new URL(this.$t('global.app_url_full'))

        return link.host !== appUrl.host
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        return false
      }
    },

    target() {
      return this.href && this.isExternal ? '_blank' : '_self'
    },

    rel() {
      return this.isExternal ? 'noopener' : null
    },

    isBButtonDisabled() {
      return this.appearance === 'b-button' && this.$attrs?.disabled
    },

    isLinkAppearance() {
      return ['a', 'b-link'].includes(this.appearance)
    },

    hasDownloadAttribute() {
      return Object.prototype.hasOwnProperty.call(this.$attrs, 'download')
    },

    isDownloadLink() {
      // For dropdown-items, we expect that the correct icon is set in the slot
      return this.appearance !== 'b-dropdown-item' && !this.forceHideDownloadIcon && this.hasDownloadAttribute
    },
  },

  created() {
    if (!this.href) throw new Error('"href" prop must be provided')
  },

  mounted() {
    if (!this.$hasSlotContent(this.$slots['default'])) throw new Error('Slot content must not be empty.')

    const slotContent = this.$slots.default()
    const isImg = slotContent[0].tag === 'img'
    const imgHasAlt = isImg && slotContent[0].props?.alt
    const isInvalidContent = slotContent.length === 1 && (slotContent[0].tag === 'i' || (isImg && !imgHasAlt))
    if (isInvalidContent) {
      throw new Error(
        'Slot content must not only contain an icon or an `<img>` without alt text. It should also provide a text for context (can be hidden with "sr-only" class).',
      )
    }

    /*
     * Remove 'role="button"' & 'tabindex="0"' attributes, if appearance is 'b-button'.
     * Which are beeing added by bootstrap automatically (what we dont want).
     */
    if (this.appearance === 'b-button') {
      this.$el.removeAttribute('role')
      this.$el.removeAttribute('tabindex')
    }
  },
}
</script>

<style scoped lang="scss">
.buttonDisabled.btn[aria-disabled='true'] {
  -webkit-filter: unset !important;
  filter: unset !important;
  opacity: 0.65 !important;
}

// fix small layout shift
[data-test='download-icon'] {
  transform: translateY(-0.5px);
}
</style>
